import React, { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { createNudge } from "../../api/nudges";
import {
  fetchBriefingSettings,
  saveBriefingSettings,
  saveEveningSettings,
} from "../../api/briefingSettings";
import { APP_HOME } from "../../lib/appRoutes";
import { SCHEDULE_LABEL } from "../../lib/scheduleLabels";
import { userIsAdmin } from "../../lib/adminImport";
import { supabase } from "../../lib/supabase";
import { openInstallPrompt } from "../../hooks/useInstallPrompt";
import { isIosDevice } from "../../lib/installPrompt";
import { usePushNotifications } from "../../hooks/usePushNotifications";
import "./NotificationSettingsCard.css";

const DEFAULT_MORNING_TIME = "07:00";
const DEFAULT_EVENING_TIME = "20:00";
const ADMIN_CHANNELS = [
  { id: "push", label: "Push" },
  { id: "email", label: "E-Mail" },
  { id: "both", label: "Beides" },
];

function readAdminChannel(user) {
  const raw = user?.user_metadata?.admin_notification_channel;
  return ADMIN_CHANNELS.some((item) => item.id === raw) ? raw : "";
}

function SettingsRow({
  label,
  enabled,
  sendTime,
  disabled,
  saving,
  onToggle,
  onTimeChange,
  onTimeCommit,
}) {
  return (
    <div className="notification-settings-row">
      <span className="notification-settings-row-label">{label}</span>
      <div className="notification-settings-row-controls">
        <button
          type="button"
          className={`notification-settings-toggle${enabled ? " notification-settings-toggle--on" : ""}`}
          role="switch"
          aria-checked={enabled}
          aria-label={enabled ? `${label} ausschalten` : `${label} einschalten`}
          onClick={onToggle}
          disabled={disabled || saving}
        >
          <span className="notification-settings-toggle-track" aria-hidden="true">
            <span className="notification-settings-toggle-knob" />
          </span>
        </button>
        <input
          type="time"
          className="notification-settings-time"
          value={sendTime}
          onChange={onTimeChange}
          onBlur={onTimeCommit}
          disabled={disabled || saving || !enabled}
          aria-label={`Uhrzeit ${label}`}
        />
      </div>
    </div>
  );
}

export default function NotificationSettingsCard() {
  const { user } = useAuth();
  const isAdmin = userIsAdmin(user);
  const {
    supported,
    standalone,
    permission,
    subscribed,
    loading: pushLoading,
    error: pushError,
    enablePush,
    disablePush,
  } = usePushNotifications({ enabled: Boolean(user) });

  const [morningEnabled, setMorningEnabled] = useState(false);
  const [morningTime, setMorningTime] = useState(DEFAULT_MORNING_TIME);
  const [eveningEnabled, setEveningEnabled] = useState(false);
  const [eveningTime, setEveningTime] = useState(DEFAULT_EVENING_TIME);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(null);
  const [error, setError] = useState(null);
  const [testPending, setTestPending] = useState(false);
  const [adminChannel, setAdminChannel] = useState(() => readAdminChannel(user));
  const [channelSaving, setChannelSaving] = useState(false);
  const [digestOn, setDigestOn] = useState(() => !user?.user_metadata?.weekly_digest_opt_out_at);
  const [digestSaving, setDigestSaving] = useState(false);

  useEffect(() => {
    setDigestOn(!user?.user_metadata?.weekly_digest_opt_out_at);
  }, [user]);

  useEffect(() => {
    if (!user) return;
    let active = true;
    fetchBriefingSettings()
      .then((settings) => {
        if (!active) return;
        setMorningEnabled(settings.enabled);
        setMorningTime(settings.sendTime);
        setEveningEnabled(settings.eveningEnabled);
        setEveningTime(settings.eveningSendTime);
        setLoaded(true);
      })
      .catch((e) => {
        if (!active) return;
        setError(e instanceof Error ? e.message : String(e));
        setLoaded(true);
      });
    return () => {
      active = false;
    };
  }, [user]);

  if (!user) return null;

  const touchpointsDisabled = !loaded || !subscribed || saving !== null;

  const persistMorning = async (next) => {
    setSaving("morning");
    setError(null);
    try {
      await saveBriefingSettings(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  const persistEvening = async (next) => {
    setSaving("evening");
    setError(null);
    try {
      await saveEveningSettings(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(null);
    }
  };

  const handleMorningToggle = async () => {
    const next = { enabled: !morningEnabled, sendTime: morningTime };
    setMorningEnabled(next.enabled);
    await persistMorning(next);
  };

  const handleEveningToggle = async () => {
    const next = { enabled: !eveningEnabled, sendTime: eveningTime };
    setEveningEnabled(next.enabled);
    await persistEvening(next);
  };

  const handleMorningTimeCommit = async () => {
    if (!morningEnabled) return;
    await persistMorning({ enabled: morningEnabled, sendTime: morningTime });
  };

  const handleEveningTimeCommit = async () => {
    if (!eveningEnabled) return;
    await persistEvening({ enabled: eveningEnabled, sendTime: eveningTime });
  };

  const handleEnablePush = async () => {
    setError(null);
    try {
      await enablePush();
    } catch {
      /* hook sets pushError */
    }
  };

  const handleDisablePush = async () => {
    setError(null);
    try {
      await disablePush();
    } catch {
      /* hook sets pushError */
    }
  };

  const handleDigestToggle = async () => {
    if (digestSaving) return;
    const next = !digestOn;
    setDigestSaving(true);
    setError(null);
    setDigestOn(next);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: { weekly_digest_opt_out_at: next ? null : new Date().toISOString() },
      });
      if (updateError) throw updateError;
    } catch (e) {
      setDigestOn(!next);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setDigestSaving(false);
    }
  };

  const handleAdminChannel = async (next) => {
    if (next === adminChannel || channelSaving) return;
    setChannelSaving(true);
    setError(null);
    const previous = adminChannel;
    setAdminChannel(next);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        data: { admin_notification_channel: next },
      });
      if (updateError) throw updateError;
    } catch (e) {
      setAdminChannel(previous);
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setChannelSaving(false);
    }
  };

  const handleTestNudge = async () => {
    setError(null);
    setTestPending(true);
    try {
      const fireAt = new Date(Date.now() + 2 * 60 * 1000);
      await createNudge({
        fireAt,
        title: "Kurzer Test von Albis",
        body: "Wenn du das siehst, funktionieren Erinnerungen.",
        url: APP_HOME,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setTestPending(false);
    }
  };

  const showGate = supported && permission !== "denied" && !subscribed;
  const needsHomeScreen = isIosDevice() && !standalone;

  return (
    <section className="notification-settings-card" aria-labelledby="notification-settings-title">
      <h3 id="notification-settings-title" className="notification-settings-title">
        Erinnerungen
      </h3>
      <p className="notification-settings-hint">
        Kurz anstupsen — Morgen-Briefing, Abend-Auszeit, {SCHEDULE_LABEL}.
      </p>

      {!supported ? (
        <p className="notification-settings-gate-text">
          In diesem Browser sind Push-Erinnerungen nicht verfügbar.
        </p>
      ) : null}

      {supported && permission === "denied" ? (
        <p className="notification-settings-gate-text">
          Benachrichtigungen sind blockiert — in den Geräteeinstellungen für Albis erlauben.
        </p>
      ) : null}

      {showGate && needsHomeScreen ? (
        <div className="notification-settings-gate">
          <p className="notification-settings-gate-text">
            Albis zum Home-Bildschirm hinzufügen, dann Push aktivieren.
          </p>
          <button
            type="button"
            className="notification-settings-btn notification-settings-btn--secondary"
            onClick={openInstallPrompt}
          >
            Zum Home-Bildschirm
          </button>
        </div>
      ) : null}

      {showGate && !needsHomeScreen ? (
        <div className="notification-settings-gate">
          <p className="notification-settings-gate-text">Push-Berechtigung fehlt noch.</p>
          <button
            type="button"
            className="notification-settings-btn"
            onClick={handleEnablePush}
            disabled={pushLoading}
          >
            {pushLoading ? "Wird eingerichtet…" : "Aktivieren"}
          </button>
        </div>
      ) : null}

      {error || pushError ? (
        <p className="notification-settings-error" role="alert">
          {error || pushError}
        </p>
      ) : null}

      {isAdmin ? (
        <div className="notification-settings-admin">
          <div className="notification-settings-admin-label">
            <span>Zustellung</span>
            <span className="mehr-list-admin-badge">Admin</span>
          </div>
          <p className="notification-settings-hint">
            Nur für dich. Ohne Wahl: Push, sonst E-Mail.
          </p>
          <div className="notification-settings-channel" role="group" aria-label="Zustellung">
            {ADMIN_CHANNELS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`notification-settings-channel-btn${
                  adminChannel === item.id ? " notification-settings-channel-btn--on" : ""
                }`}
                aria-pressed={adminChannel === item.id}
                disabled={channelSaving}
                onClick={() => handleAdminChannel(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}

      <div className="notification-settings-row notification-settings-digest">
        <span className="notification-settings-row-copy">
          <span className="notification-settings-row-label">Sonntagsmail</span>
          <span className="notification-settings-row-note">Sonntagabend per Mail.</span>
        </span>
        <button
          type="button"
          className={`notification-settings-toggle${digestOn ? " notification-settings-toggle--on" : ""}`}
          role="switch"
          aria-checked={digestOn}
          aria-label={digestOn ? "Sonntagsmail ausschalten" : "Sonntagsmail einschalten"}
          onClick={handleDigestToggle}
          disabled={digestSaving}
        >
          <span className="notification-settings-toggle-track" aria-hidden="true">
            <span className="notification-settings-toggle-knob" />
          </span>
        </button>
      </div>

      <div className="notification-settings-rows">
        <SettingsRow
          label="Morgen-Briefing"
          enabled={morningEnabled}
          sendTime={morningTime}
          disabled={touchpointsDisabled}
          saving={saving === "morning"}
          onToggle={handleMorningToggle}
          onTimeChange={(e) => setMorningTime(e.target.value || DEFAULT_MORNING_TIME)}
          onTimeCommit={handleMorningTimeCommit}
        />
        <SettingsRow
          label="Abend-Auszeit"
          enabled={eveningEnabled}
          sendTime={eveningTime}
          disabled={touchpointsDisabled}
          saving={saving === "evening"}
          onToggle={handleEveningToggle}
          onTimeChange={(e) => setEveningTime(e.target.value || DEFAULT_EVENING_TIME)}
          onTimeCommit={handleEveningTimeCommit}
        />
      </div>

      {subscribed ? (
        <div className="notification-settings-footer">
          <button
            type="button"
            className="notification-settings-link"
            onClick={handleDisablePush}
            disabled={pushLoading}
          >
            {pushLoading ? "Wird deaktiviert…" : "Push deaktivieren"}
          </button>
          {isAdmin ? (
            <button
              type="button"
              className="notification-settings-link"
              onClick={handleTestNudge}
              disabled={testPending || pushLoading}
            >
              {testPending ? "Test wird geplant…" : "Test in 2 Min."}
            </button>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}
